import { useEffect, useRef } from 'react';

import { prefersReducedMotion } from 'src/utils/motion';

const PALETTE = [
  [100, 91, 232],
  [170, 164, 255],
  [155, 240, 201]
];

const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));

// 16-step gradient violet → lavender → mint
const COLORS = Array.from({ length: 16 }, (_, k) => {
  const t = k / 15;
  const c = t < 0.5 ? mix(PALETTE[0], PALETTE[1], t * 2) : mix(PALETTE[1], PALETTE[2], (t - 0.5) * 2);
  return `rgb(${c.join(',')})`;
});

const pointer = { x: 0, y: 0, bound: false };

const trackPointer = () => {
  if (pointer.bound) return;
  pointer.bound = true;
  window.addEventListener(
    'pointermove',
    (e) => {
      pointer.x = e.clientX / window.innerWidth - 0.5;
      pointer.y = e.clientY / window.innerHeight - 0.5;
    },
    { passive: true }
  );
};

// Points evenly spread on a sphere (golden angle)
const createPoints = (count) => {
  const angle = Math.PI * (3 - Math.sqrt(5));
  return Array.from({ length: count }, (_, i) => {
    const y = 1 - (i / (count - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    return [Math.cos(angle * i) * r, y, Math.sin(angle * i) * r, Math.random() * 6.283];
  });
};

export default function Orb({ count = 1400, scale = 0.34, glow = 1, mode = 'idle', className }) {
  const canvasRef = useRef(null);
  const modeRef = useRef(mode);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const points = createPoints(count);
    const s = { w: 0, h: 0, d: 1, level: 0, rot: 0, mx: 0, my: 0, visible: true };

    trackPointer();

    const draw = (t) => {
      const W = s.w * s.d;
      const H = s.h * s.d;
      const mode = modeRef.current;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.clearRect(0, 0, W, H);

      const target =
        mode === 'speak'
          ? 0.42 + 0.58 * Math.abs(Math.sin(t * 7.3) * Math.sin(t * 2.9 + 1.3))
          : mode === 'think'
            ? 0.2
            : 0.07 + 0.03 * Math.sin(t * 1.4);
      s.level += (target - s.level) * 0.09;
      s.mx += (pointer.x - s.mx) * 0.04;
      s.my += (pointer.y - s.my) * 0.04;

      const L = s.level;
      const R = Math.min(W, H) * scale;
      const cx = W / 2;
      const cy = H / 2;

      const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 1.9);
      gradient.addColorStop(0, `rgba(100,91,232,${(0.28 + L * 0.3) * glow})`);
      gradient.addColorStop(0.45, `rgba(100,91,232,${(0.07 + L * 0.1) * glow})`);
      gradient.addColorStop(1, 'rgba(100,91,232,0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, W, H);

      s.rot += mode === 'think' ? 0.028 : 0.0045;
      const ry = s.rot + s.mx * 0.6;
      const rx = 0.38 + s.my * 0.4;
      const cY = Math.cos(ry);
      const sY = Math.sin(ry);
      const cX = Math.cos(rx);
      const sX = Math.sin(rx);
      const focal = 3.2;
      const base = 1.5 * s.d;

      ctx.globalCompositeOperation = 'lighter';
      for (const p of points) {
        const ph = p[3];
        const disp =
          1 +
          L * 0.17 * Math.sin(p[1] * 7 + t * 5 + ph) +
          L * 0.08 * Math.sin(p[0] * 9 - t * 3.7) +
          0.022 * Math.sin(t * 1.2 + ph);
        const x = p[0] * disp;
        const y = p[1] * disp;
        const z = p[2] * disp;
        const x1 = x * cY - z * sY;
        const z1 = x * sY + z * cY;
        const y1 = y * cX - z1 * sX;
        const z2 = y * sX + z1 * cX;
        const persp = focal / (focal - z2);
        const depth = (z2 + 1.2) / 2.4;
        const ci = Math.max(0, Math.min(15, (((y1 + 1) / 2) * 15 + L * 5) | 0));
        const size = base * (0.45 + depth) * persp;

        ctx.globalAlpha = Math.min(1, 0.1 + depth * 0.8);
        ctx.fillStyle = COLORS[ci];
        ctx.fillRect(cx + x1 * R * persp - size / 2, cy + y1 * R * persp - size / 2, size, size);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      s.d = Math.min(window.devicePixelRatio || 1, 2);
      s.w = rect.width;
      s.h = rect.height;
      canvas.width = Math.max(1, Math.round(rect.width * s.d));
      canvas.height = Math.max(1, Math.round(rect.height * s.d));
      if (prefersReducedMotion) draw(1.2);
    };

    const resizeObserver = new ResizeObserver(resize);
    const visibilityObserver = new IntersectionObserver(([entry]) => (s.visible = entry.isIntersecting));
    resizeObserver.observe(canvas);
    visibilityObserver.observe(canvas);
    resize();

    let raf = 0;
    if (!prefersReducedMotion) {
      const t0 = performance.now();
      const loop = (now) => {
        raf = requestAnimationFrame(loop);
        if (s.visible && s.w) draw((now - t0) / 1000);
      };
      raf = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
    };
  }, [count, scale, glow]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden='true'
    />
  );
}
